import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getDisputeStatusConfig,
  DISPUTE_TYPE_LABELS,
  getDisputeReasonLabel,
  formatDisputeNumber
} from '../../constants/disputeConstants';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import { formatDate } from '../../utils/dateUtils';

const DisputesList = ({
  disputes = [],
  loading = false,
  title = 'Disputes',
  emptyMessage = 'No disputes found',
  showFiler = true,
  showRespondent = true,
  compact = false
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(true);

  const handleViewDetails = (disputeId) => {
    navigate(`/disputes/${disputeId}`);
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }}>Loading disputes...</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GavelIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6">{title}</Typography>
          {disputes.length > 0 && (
            <Chip
              label={disputes.length}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <IconButton
          onClick={() => setExpanded(!expanded)}
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: '0.3s'
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {disputes.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {emptyMessage}
          </Alert>
        ) : (
          <TableContainer sx={{ mt: 2 }}>
            <Table size={compact ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Dispute #</TableCell>
                  <TableCell>Title</TableCell>
                  {!isMobile && <TableCell>Dispute Type</TableCell>}
                  {showFiler && !isMobile && <TableCell>Filed By</TableCell>}
                  {showRespondent && !isMobile && <TableCell>Against</TableCell>}
                  <TableCell>Status</TableCell>
                  {!isMobile && <TableCell>Created</TableCell>}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {disputes.map((dispute) => {
                  const statusConfig = getDisputeStatusConfig(dispute.status);
                  return (
                    <TableRow key={dispute.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatDisputeNumber(dispute.disputeNumber)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {dispute.title}
                        </Typography>
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Chip
                            label={DISPUTE_TYPE_LABELS[dispute.disputeType]}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      )}
                      {showFiler && !isMobile && (
                        <TableCell>
                          <Typography variant="body2">
                            {dispute.filerName}
                          </Typography>
                        </TableCell>
                      )}
                      {showRespondent && !isMobile && (
                        <TableCell>
                          <Typography variant="body2">
                            {dispute.respondentName}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={statusConfig.label}
                          size="small"
                          sx={{
                            bgcolor: `${statusConfig.color}.100`,
                            color: `${statusConfig.color}.800`,
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(dispute.createdAt)}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(dispute.id)}
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Paper>
  );
};

export default DisputesList;
